// HierarchyManager.tsx
import React, { useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import {
	Box,
	TextField,
	Button as MuiButton,
	Typography,
	Paper,
	IconButton,
	CircularProgress,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import styles from '../GeneralSettings.module.scss';
import { SimpleTreeView as TreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { Add, Delete } from '@mui/icons-material';
import { useHierarchy, HierarchyTreeNode } from 'core/hooks/useHierarchy';
import {
	createHierarchyNode,
	moveHierarchyNode,
	deleteHierarchyNode,
	CreateHierarchyNodePayload,
	MoveHierarchyNodePayload,
} from 'core/API/hierarchy';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
	DragStartEvent,
	DragOverEvent,
	DragOverlay,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { useToastContext } from '../../../../../core/components/Toast/context/ToastContext';
import { Button } from '../../../../../components/ui/button';

interface HierarchyNode {
	id: string;
	name: string;
	type: 'organization' | 'custom' | 'workplace' | 'group';
	children?: HierarchyNode[];
	parentId?: string;
	ownership?: 'system' | 'organization'; // For workplaces only
}

const MAX_NAME_LENGTH = 25;
const MAX_CUSTOM_LEVELS = 10;

interface PendingMove {
	nodeId: string;
	oldParentId: string;
	newParentId: string;
}

interface PendingNewNode {
	tempId: string; // Temporary ID for local tracking
	parentId: string;
	name: string;
	type: string;
}

interface HierarchyManagerProps {
	selectedNodeId?: string | null;
	handleBackClick: () => void;
	handleSave: () => void;
}

const HierarchyManager: React.FC<HierarchyManagerProps> = ({
	selectedNodeId: _propSelectedNodeId,
	handleBackClick,
	handleSave,
}) => {
	// Use the hierarchy hook to fetch data from API
	const { hierarchyData, loading, error, buildHierarchyTree, refetch } = useHierarchy({
		page: 1,
		itemsPerPage: 50,
	});

	const [tree, setTree] = useState<HierarchyNode | null>(null);
	const [selectedNodeId, setSelectedNodeId] = useState<string>('');
	const [expanded, setExpanded] = useState<string[]>([]);
	const [newNodeName, setNewNodeName] = useState('');
	const [activeId, setActiveId] = useState<string | null>(null);
	const [pendingMoves, setPendingMoves] = useState<PendingMove[]>([]);
	const [pendingNewNodes, setPendingNewNodes] = useState<PendingNewNode[]>([]);
	const [pendingDeletes, setPendingDeletes] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const { showToast } = useToastContext();

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Require 8px of movement before activating
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	// Convert HierarchyTreeNode to local HierarchyNode format
	const convertToLocalFormat = useCallback((node: HierarchyTreeNode): HierarchyNode => {
		return {
			id: String(node.id),
			name: node.name,
			type: node.type,
			children: node.children.map(convertToLocalFormat),
			parentId: node.parent ? String(node.parent.id) : undefined,
			ownership: node.type === 'workplace' ? 'organization' : undefined,
		};
	}, []);

	// Update local tree when hierarchy data changes
	useEffect(() => {
		if (hierarchyData) {
			const hierarchyTree = buildHierarchyTree();
			if (hierarchyTree.length > 0) {
				const rootNode = convertToLocalFormat(hierarchyTree[0]);
				setTree(rootNode);
				setSelectedNodeId(rootNode.id);
				setExpanded([rootNode.id]);
				// Clear any pending changes when fresh data is loaded
				setPendingMoves([]);
				setPendingNewNodes([]);
			}
		}
	}, [hierarchyData, buildHierarchyTree, convertToLocalFormat]);

	// Show loading state
	if (loading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '400px',
				}}
			>
				<CircularProgress size={20} color="inherit" />
			</Box>
		);
	}
	
	// Show error state
	if (error) {
		return (
			<Box
				sx={{
					padding: '20px',
					textAlign: 'center',
					color: 'red',
				}}
			>
				<p>Error loading hierarchy: {error}</p>
				<Button onClick={refetch} className={styles.backButton}>
					Retry
				</Button>
			</Box>
		);
	}

	// Show empty state
	if (!tree) {
		return (
			<Box
				sx={{
					padding: '20px',
					textAlign: 'center',
					color: '#666',
				}}
			>
				<p>No hierarchy data available.</p>
			</Box>
		);
	}

	// Helper function to find a node by ID
	const findNode = (node: HierarchyNode, targetId: string): HierarchyNode | null => {
		if (node.id === targetId) return node;
		for (const child of node.children || []) {
			const found = findNode(child, targetId);
			if (found) return found;
		}
		return null;
	};

	// Helper function to count custom levels from organization to a node
	const getCustomLevelDepth = (nodeId: string): number => {
		const node = findNode(tree, nodeId);
		if (!node) return 0;

		let depth = 0;
		let currentNode = node;

		// Traverse up to organization, counting custom levels
		while (currentNode && currentNode.type !== 'organization') {
			if (currentNode.type === 'custom') depth++;
			// Find parent
			const parent = findParentNode(tree, currentNode.id);
			currentNode = parent;
		}

		return depth;
	};

	// Helper function to find parent node
	const findParentNode = (node: HierarchyNode, childId: string): HierarchyNode | null => {
		for (const child of node.children || []) {
			if (child.id === childId) return node;
			const found = findParentNode(child, childId);
			if (found) return found;
		}
		return null;
	};

	// Check if a node can have children based on type and hierarchy rules
	const canHaveChildren = (nodeId: string): boolean => {
		const node = findNode(tree, nodeId);
		if (!node) return false;

		// Workplaces cannot have children
		if (node.type === 'workplace') return false;

		// Check depth limit for custom levels
		const currentDepth = getCustomLevelDepth(nodeId);
		return currentDepth < MAX_CUSTOM_LEVELS;
	};

	// Check if a node can be moved to another parent
	const canMoveNodeTo = (nodeToMove: HierarchyNode, targetParentId: string): boolean => {
		const targetParent = findNode(tree, targetParentId);
		if (!targetParent) return false;

		// Cannot move to itself or its own children
		if (nodeToMove.id === targetParentId || isDescendant(nodeToMove.id, targetParentId)) {
			return false;
		}

		// Cannot move organization node
		if (nodeToMove.type === 'organization') return false;

		// Target must be able to have children of this type
		return canHaveChildren(targetParentId);
	};

	// Check if a node is a descendant of another node
	const isDescendant = (ancestorId: string, nodeId: string): boolean => {
		const ancestor = findNode(tree, ancestorId);
		if (!ancestor) return false;

		const checkChildren = (node: HierarchyNode): boolean => {
			if (node.id === nodeId) return true;
			return node.children?.some(checkChildren) || false;
		};

		return checkChildren(ancestor);
	};

	// Move a node to a new parent
	const moveNode = (nodeId: string, newParentId: string) => {
		const nodeToMove = findNode(tree, nodeId);
		if (!nodeToMove || !canMoveNodeTo(nodeToMove, newParentId)) return;

		// Track the move for later saving
		const oldParentId = nodeToMove.parentId || '';

		// Remove any existing pending move for this node
		setPendingMoves((prev) => prev.filter((move) => move.nodeId !== nodeId));

		// Add new pending move (only if it's actually different from original position)
		if (oldParentId !== newParentId) {
			setPendingMoves((prev) => [
				...prev,
				{
					nodeId,
					oldParentId,
					newParentId,
				},
			]);
		}

		// Remove node from its current parent
		const removeFromParent = (node: HierarchyNode): HierarchyNode => {
			return {
				...node,
				children: node.children?.filter((child) => child.id !== nodeId).map(removeFromParent) || [],
			};
		};

		// Add node to new parent
		const addToNewParent = (node: HierarchyNode): HierarchyNode => {
			if (node.id === newParentId) {
				return {
					...node,
					children: [...(node.children || []), { ...nodeToMove, parentId: newParentId }],
				};
			}
			return {
				...node,
				children: node.children?.map(addToNewParent) || [],
			};
		};

		const updatedTree = addToNewParent(removeFromParent(tree));
		setTree(updatedTree);
		setExpanded((prev) => [...new Set([...prev, newParentId])]);
	};

	// Save all pending changes to the backend
	const saveChanges = async () => {
		if (pendingMoves.length === 0 && pendingNewNodes.length === 0 && pendingDeletes.length === 0) {
			console.log('No changes to save');
			return;
		}

		setIsSaving(true);

		try {
			console.log('Saving pending changes:', {
				moves: pendingMoves,
				newNodes: pendingNewNodes,
				deletes: pendingDeletes,
			});

			const allResults: any[] = [];
			// Track mapping from temporary IDs to real IDs
			const tempIdToRealIdMap: { [tempId: string]: string } = {};

			// First, create new nodes
			if (pendingNewNodes.length > 0) {
				// console.log('Creating new nodes...');
				for (const newNode of pendingNewNodes) {
					// Determine the parent node type to set correct hierarchyTypeParent
					// const parentNode = findNode(tree, newNode.parentId);
					const hierarchyTypeParent = '/api/hierarchy_types/3'; // For custom node parent

					const payload: CreateHierarchyNodePayload = {
						parentId: parseInt(newNode.parentId, 10) || 0,
						name: newNode.name,
						hierarchyTypeParent,
						hierarchyTypeChild: '/api/hierarchy_types/3', // Always /api/hierarchy_types/3 for new custom node
						childId: null,
						editable: true,
					};

					// console.log(`Creating new node: ${newNode.name} under parent ${newNode.parentId}`);
					const result = await createHierarchyNode(payload);
					allResults.push({ type: 'create', item: newNode, result });

					if (result.success && result.id) {
						// Map temporary ID to real ID from API response
						tempIdToRealIdMap[newNode.tempId] = result.id.toString();
						console
							.log
							// `Mapped temp ID ${newNode.tempId} to real ID ${result.id} with hierarchyTypeChild /api/hierarchy_types/3`
							();
					} else {
						console.error(`Failed to create node ${newNode.name}:`, result.message);
					}
				}
			}

			// Update pending moves to use real IDs instead of temporary IDs (nodeId, oldParentId, newParentId)
			const updatedMoves = pendingMoves.map((move) => {
				const newMove = { ...move };

				// Replace nodeId if it was a temp id and we have a mapping
				if (tempIdToRealIdMap[move.nodeId]) {
					newMove.nodeId = tempIdToRealIdMap[move.nodeId];
				}

				// Replace oldParentId if it's a temp id
				if (tempIdToRealIdMap[move.oldParentId]) {
					newMove.oldParentId = tempIdToRealIdMap[move.oldParentId];
				}

				// Replace newParentId if it's a temp id
				if (tempIdToRealIdMap[move.newParentId]) {
					newMove.newParentId = tempIdToRealIdMap[move.newParentId];
				}

				return newMove;
			});

			// Then, process moves with updated parent IDs
			if (updatedMoves.length > 0) {
				// console.log('Processing moves...');
				for (const move of updatedMoves) {
					// If the nodeId is still a temp id (means create failed earlier), skip this move and mark as failed
					if (String(move.nodeId).startsWith('temp_') && !tempIdToRealIdMap[move.nodeId]) {
						const msg = `Skipping move for ${move.nodeId} because the node was not created successfully.`;
						console.error(msg);
						allResults.push({ type: 'move', item: move, result: { success: false, message: msg } });
						continue;
					}

					const payload: MoveHierarchyNodePayload = {
						newParentId: parseInt(move.newParentId, 10) || 0,
						childId: null, // As per API specification
					};

					// console.log(
					//  `Processing move: node ${move.nodeId} from ${move.oldParentId} to ${move.newParentId}`
					// );
					const result = await moveHierarchyNode(move.nodeId, payload);
					allResults.push({ type: 'move', item: move, result });

					if (!result.success) {
						console.error(`Failed to move node ${move.nodeId}:`, result.message);
					}
				}
			}

			// Finally, process deletes
			if (pendingDeletes.length > 0) {
				// console.log('Processing deletes:', pendingDeletes);
				for (const nodeId of pendingDeletes) {
					// If nodeId is a tempId, skip (never created)
					if (tempIdToRealIdMap[nodeId]) {
						// If node was just created, delete by real ID
						const realId = tempIdToRealIdMap[nodeId];
						// console.log('Calling deleteHierarchyNode for just-created node:', realId);
						const result = await deleteHierarchyNode(realId);
						allResults.push({ type: 'delete', item: nodeId, result });
					} else if (!nodeId.startsWith('temp_')) {
						// If node is a real node, delete by its ID
						// console.log('Calling deleteHierarchyNode for real node:', nodeId);
						const result = await deleteHierarchyNode(nodeId);
						allResults.push({ type: 'delete', item: nodeId, result });
					} else {
						// If node is a temp node and never created, just skip
						// console.log('Skipping delete for temp node (never created):', nodeId);
						allResults.push({
							type: 'delete',
							item: nodeId,
							result: { success: true, skipped: true },
						});
					}
				}
			}

			// Check results
			const failedOperations = allResults.filter((r) => !r.result.success);

			if (failedOperations.length === 0) {
				// console.log('All operations completed successfully');
				// Clear all pending changes
				setPendingMoves([]);
				setPendingNewNodes([]);
				setPendingDeletes([]);
				// Refetch hierarchy to ensure consistency
				refetch();

				showToast('success', 'Hierarchy changes saved successfully!');
			} else {
				console.error('Some operations failed:', failedOperations);
				showToast(
					'error',
					`${failedOperations.length} out of ${allResults.length} operations failed. Please check the console for details.`
				);
			}
		} catch (error) {
			console.error('Error saving hierarchy changes:', error);
			showToast('error', 'An error occurred while saving changes. Please try again.');
		} finally {
			setIsSaving(false);
			handleSave();
		}
	};

	// Reset all pending changes and reload original data
	const resetChanges = async () => {
		// console.log('Resetting all pending changes');
		setPendingMoves([]);
		setPendingNewNodes([]);
		refetch(); // This will reload the original hierarchy from the API
	};

	// Check if a node has pending changes
	const hasUnsavedChanges = (nodeId: string): boolean => {
		return (
			pendingMoves.some((move) => move.nodeId === nodeId) ||
			pendingNewNodes.some((node) => node.tempId === nodeId) ||
			pendingDeletes.includes(nodeId)
		);
	};

	// Drag and drop handlers for @dnd-kit
	const handleDragStart = (event: DragStartEvent) => {
		const { active } = event;
		setActiveId(active.id as string);
		// console.log('Drag started for:', active.id);
	};

	const handleDragOver = (_event: DragOverEvent) => {
		// Handle drag over if needed for hover effects
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over || active.id === over.id) {
			setActiveId(null);
			return;
		}

		const draggedNodeId = active.id as string;
		const targetNodeId = over.id as string;

		const draggedNode = findNode(tree, draggedNodeId);

		if (draggedNode && canMoveNodeTo(draggedNode, targetNodeId)) {
			moveNode(draggedNodeId, targetNodeId);
			// console.log('Move completed:', draggedNodeId, '->', targetNodeId);
		}

		setActiveId(null);
	};

	// Draggable component for drag handles
	const DraggableHandle: React.FC<{ node: HierarchyNode }> = ({ node }) => {
		const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
			id: node.id,
			data: {
				type: 'hierarchy-node',
				node,
			},
		});

		if (node.type === 'organization') {
			return null;
		}

		return (
			<Box
				ref={setNodeRef}
				component="span"
				sx={{
					fontSize: '12px',
					opacity: isDragging ? 0.5 : 0.6,
					cursor: 'grab',
					userSelect: 'none',
					'&:hover': { opacity: 1 },
					'&:active': { cursor: 'grabbing' },
				}}
				{...listeners}
				{...attributes}
			>
				⋮⋮
			</Box>
		);
	};

	// Droppable component for drop zones
	const DroppableNode: React.FC<{
		node: HierarchyNode;
		children: React.ReactNode;
		isDraggedNode: boolean;
	}> = ({ node, children, isDraggedNode }) => {
		const { isOver, setNodeRef } = useDroppable({
			id: node.id,
			data: {
				type: 'hierarchy-node',
				node,
			},
		});

		const draggedNode = activeId ? findNode(tree, activeId) : null;
		const canAcceptDrop = draggedNode ? canMoveNodeTo(draggedNode, node.id) : false;

		return (
			<Box
				ref={setNodeRef}
				sx={{
					opacity: isDraggedNode ? 0.5 : 1,
					backgroundColor: isOver && canAcceptDrop ? 'action.hover' : 'transparent',
					border: isOver && canAcceptDrop ? '2px dashed #1976d2' : '2px solid transparent',
					borderRadius: 1,
					padding: 0.5,
					transition: 'all 0.2s ease',
					'&:hover': {
						backgroundColor: isOver && canAcceptDrop ? 'action.hover' : 'action.focus',
					},
				}}
			>
				{children}
				{isOver && !canAcceptDrop && draggedNode && (
					<div style={{ fontSize: '12px', color: '#d32f2f', marginLeft: '8px' }}>
						❌ Cannot drop here
					</div>
				)}
				{isOver && canAcceptDrop && (
					<div style={{ fontSize: '12px', color: '#2e7d32', marginLeft: '8px' }}>
						✅ Drop to move here
					</div>
				)}
			</Box>
		);
	};

	const addNode = () => {
		if (!selectedNodeId || newNodeName.trim() === '') return;

		// Check if the selected node can have children
		if (!canHaveChildren(selectedNodeId)) {
			showToast(
				'error',
				`Maximum ${MAX_CUSTOM_LEVELS} custom levels allowed between Organization and Workplaces.`
			);
			return;
		}

		// Generate a temporary ID for local tracking
		const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

		// Track the new node for later saving
		const pendingNode: PendingNewNode = {
			tempId,
			parentId: selectedNodeId,
			name: newNodeName.slice(0, MAX_NAME_LENGTH),
			type: 'group', // Default type for custom hierarchy nodes
		};

		setPendingNewNodes((prev) => [...prev, pendingNode]);

		// Create new node for local state with temporary ID
		const newNode: HierarchyNode = {
			id: tempId,
			name: pendingNode.name,
			type: 'custom', // Map 'group' to 'custom' for display
			children: [],
			parentId: selectedNodeId,
		};

		const updateTree = (node: HierarchyNode): HierarchyNode => {
			if (node.id === selectedNodeId) {
				return {
					...node,
					children: [...(node.children || []), newNode],
				};
			}
			return {
				...node,
				children: node.children?.map(updateTree) || [],
			};
		};

		if (tree) {
			setTree(updateTree(tree));
		}

		// Expand the parent node to show the new child
		setExpanded((prev) => [...new Set([...prev, selectedNodeId])]);

		// Clear the input field
		setNewNodeName('');

		// console.log('Added new node locally:', {
		// 	tempId,
		// 	parentId: selectedNodeId,
		// 	name: pendingNode.name,
		// });
	};

	const deleteNode = (nodeId: string) => {
		const nodeToDelete = findNode(tree, nodeId);
		if (!nodeToDelete) return;

		// Prevent deletion of organization
		if (nodeToDelete.type === 'organization') {
			showToast('error', 'Cannot delete the organization node.');
			return;
		}

		// Prevent deletion if node has children
		if (nodeToDelete.children && nodeToDelete.children.length > 0) {
			showToast(
				'error',
				'Cannot delete a node that has children. Please remove or move its children first.'
			);
			return;
		}

		// If deleting a custom level that has workplace children, move workplaces up
		if (nodeToDelete.type === 'custom' && nodeToDelete.children) {
			const hasWorkplaces = nodeToDelete.children.some((child) => child.type === 'workplace');
			if (hasWorkplaces) {
				const confirmDelete = window.confirm(
					'This level contains workplaces. Deleting it will move the workplaces to its parent level. Continue?'
				);
				if (!confirmDelete) return;
			}
		}

		// Clean up pending moves that involve the deleted node
		setPendingMoves((prev) => {
			const filtered = prev.filter(
				(move) =>
					move.nodeId !== nodeId && move.oldParentId !== nodeId && move.newParentId !== nodeId
			);
			if (filtered.length !== prev.length) {
				// console.log('Cleaned up pending moves for deleted node:', nodeId);
				// console.log('Remaining pending moves:', filtered);
			}
			return filtered;
		});

		// Clean up pending new nodes that have this node as parent
		setPendingNewNodes((prev) => {
			const filtered = prev.filter((node) => node.parentId !== nodeId);
			if (filtered.length !== prev.length) {
				// console.log('Cleaned up pending new nodes for deleted parent:', nodeId);
			}
			return filtered;
		});

		// Track the delete for batch save
		setPendingDeletes((prev) => {
			const updated = [...prev, nodeId];
			// console.log('Pending deletes:', updated);
			return updated;
		});

		// Remove node from local tree (same as before)
		const removeNode = (node: HierarchyNode): HierarchyNode | null => {
			if (node.id === nodeId) return null;
			if (node.children?.some((child) => child.id === nodeId)) {
				const nodeToDelete = node.children.find((child) => child.id === nodeId);
				const otherChildren = node.children.filter((child) => child.id !== nodeId);
				const childrenToPromote = nodeToDelete?.children || [];
				return {
					...node,
					children: [...otherChildren, ...childrenToPromote],
				};
			}
			return {
				...node,
				children: node.children
					?.map(removeNode)
					.filter((child): child is HierarchyNode => child !== null),
			};
		};
		const updated = removeNode(tree);
		if (updated) {
			setTree(updated);
			if (nodeId === selectedNodeId && tree) {
				setSelectedNodeId(tree.id); // Select the root node
			}
		}
	};

	// Helper function to get the full path of a node
	const getNodePath = (nodeId: string): string => {
		const node = findNode(tree, nodeId);
		if (!node) return '';

		const path: string[] = [];
		let currentNode = node;

		// Build path from current node to root
		while (currentNode) {
			path.unshift(currentNode.name);
			const parent = findParentNode(tree, currentNode.id);
			currentNode = parent;
		}

		return path.join(' → ');
	};

	// Helper function to get all possible parent nodes for a workplace
	const getAllPossibleParents = (): HierarchyNode[] => {
		const parents: HierarchyNode[] = [];

		const collectParents = (node: HierarchyNode) => {
			// console.log("parent", node.type);
			if (node.type === 'organization' || node.type === 'group') {
				parents.push(node);
			}
			node.children?.forEach(collectParents);
		};

		collectParents(tree);
		return parents;
	};

	// Helper function to update workplace properties
	const updateWorkplace = (workplaceId: string, updates: Partial<HierarchyNode>) => {
		const updateNode = (node: HierarchyNode): HierarchyNode => {
			if (node.id === workplaceId) {
				return { ...node, ...updates };
			}
			return {
				...node,
				children: node.children?.map(updateNode) || [],
			};
		};

		setTree(updateNode(tree));
	};

	// Workplace Configuration Component
	const WorkplaceConfiguration: React.FC<{ workplace: HierarchyNode }> = ({ workplace }) => {
		const [ownership, setOwnership] = useState<'system' | 'organization'>(
			workplace.ownership || 'organization'
		);
		const possibleParents = getAllPossibleParents();

		const handleOwnershipChange = (newOwnership: 'system' | 'organization') => {
			setOwnership(newOwnership);
			updateWorkplace(workplace.id, { ownership: newOwnership });
		};

		const handleHierarchyChange = (newParent: string) => {
			if (newParent !== workplace.parentId) {
				moveNode(workplace.id, newParent);
			}
		};

		return (
			<>
				<h3 className={styles.formTitle} style={{ marginBottom: '16px' }}>
					Workplace Configuration
				</h3>
				<Paper sx={{ p: 2 }}>
					<Box display="flex" gap={2} flexDirection="column">
						{/* Workplace Name Display */}
						<Box>
							<div
								style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '8px' }}
							>
								🏭 {workplace.name}
							</div>
							<div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
								Path: {getNodePath(workplace.id)}
							</div>
						</Box>

						{/* Ownership Configuration */}
						<Box>
							<TextField
								size="small"
								label="Ownership Type"
								select
								disabled
								SelectProps={{ native: true }}
								value={ownership}
								onChange={(e) => handleOwnershipChange(e.target.value as 'system' | 'organization')}
								fullWidth
								helperText="Defines who owns and manages this workplace"
							>
								<option value="organization">Organization Owned</option>
								<option value="system">System Owned</option>
							</TextField>
						</Box>

						{/* Hierarchy Configuration */}
						<Box>
							<TextField
								size="small"
								label="Parent Level"
								select
								SelectProps={{ native: true }}
								value={workplace.parentId || ''}
								onChange={(e) => handleHierarchyChange(e.target.value)}
								fullWidth
								helperText="Choose which level this workplace belongs to"
							>
								{possibleParents.map((parent) => (
									<option key={parent.id} value={parent.id}>
										{parent.name}
									</option>
								))}
							</TextField>
						</Box>

						{/* Ownership Description */}
						{/* <Box mt={2} p={2} sx={{ backgroundColor: 'background.default', borderRadius: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#333', marginBottom: '8px' }}>
                Ownership Details
              </div>
              {ownership === 'system' ? (
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>System Owned:</strong> This workplace is managed by the system. 
                  System-owned workplaces have centralized management and standardized configurations.
                </div>
              ) : (
                <div style={{ fontSize: '14px', color: '#666' }}>
                  <strong>Organization Owned:</strong> This workplace is managed by your organization. 
                  Organization-owned workplaces can have custom configurations and local management.
                </div>
              )}
            </Box> */}
					</Box>
				</Paper>
			</>
		);
	};

	const renderTree = (node: HierarchyNode) => {
		// console.log('here is node', node);
		const getNodeColor = (type: string) => {
			switch (type) {
				case 'organization':
					return '#1976d2';
				case 'custom':
					return '#757575';
				case 'workplace':
					return '#4caf50';
				default:
					return '#000';
			}
		};

		const isDraggedNode = activeId === node.id;

		return (
			<TreeItem
				key={node.id}
				itemId={node.id}
				label={
					<DroppableNode node={node} isDraggedNode={isDraggedNode}>
						<Box display="flex" alignItems="center" justifyContent="space-between">
							<Box display="flex" alignItems="center" gap={1}>
								<DraggableHandle node={node} />
								{/* <span>{getNodeIcon(node.type)}</span> */}
								<Typography
									variant="body2"
									sx={{
										color: getNodeColor(node.type),
										fontWeight: node.type === 'organization' ? 'bold' : 'normal',
										userSelect: 'none',
										fontSize: '14px',
									}}
								>
									{node.name}
								</Typography>
								{hasUnsavedChanges(node.id) && (
									<Box
										sx={{
											width: 8,
											height: 8,
											borderRadius: '50%',
											backgroundColor: 'warning.main',
											title: 'Unsaved changes',
										}}
									/>
								)}
								{/* <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'capitalize' }}>
                  ({node.type === 'organization' ? 'Organization' : node.type === 'workplace' ? 'Workplace' : 'Level'})
                </Typography> */}
							</Box>
							{!_.includes(['workplace', 'organization'], node.type) && (
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										deleteNode(node.id);
									}}
									sx={{
										opacity: 0.7,
										'&:hover': { opacity: 1 },
									}}
								>
									<Delete fontSize="small" />
								</IconButton>
							)}
						</Box>
					</DroppableNode>
				}
			>
				{node.children?.map((child) => renderTree(child))}
			</TreeItem>
		);
	};

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragStart={handleDragStart}
			onDragOver={handleDragOver}
			onDragEnd={handleDragEnd}
		>
			<Grid item direction="column" spacing={2}>
				<Grid container direction="row" justifyContent="space-between">
					<Grid xs={6}>
						<Button
							className={styles.backButton}
							onClick={handleBackClick}
							disabled={isSaving}
							// startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
						>
							Back
						</Button>
					</Grid>
					<Grid xs={6}>
						<Grid container justifyContent={'flex-end'}>
							<Grid sx={{ marginRight: '8px' }}>
								<Button className={styles.backButton} onClick={resetChanges} disabled={isSaving}>
									Reset
								</Button>
							</Grid>
							<Grid>
								<Button
									className={styles.backButton}
									onClick={async () => {
										// If there are pending changes, confirm first. Otherwise, save directly.
										await saveChanges();
									}}
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				<Grid xs={12} container direction="row" justifyContent="flex-start" alignItems="center">
					<h2 className={styles.formTitle}>Edit Hierarchy</h2>
				</Grid>
			</Grid>
			<Box sx={{ p: 2 }}>
				{/* Add New Level Section - First */}
				<Box mb={3}>
					<h3 className={styles.formTitle} style={{ marginBottom: '16px' }}>
						Add New Level
					</h3>
					<Paper sx={{ p: 2 }}>
						<Box display="flex" gap={2} flexDirection="column">
							<TextField
								size="small"
								label="New Level Name"
								value={newNodeName}
								onChange={(e) => setNewNodeName(e.target.value.slice(0, MAX_NAME_LENGTH))}
								helperText={`${newNodeName.length}/${MAX_NAME_LENGTH} characters`}
								fullWidth
							/>
							<MuiButton
								className={styles.backButton}
								startIcon={<Add />}
								onClick={addNode}
								disabled={!newNodeName.trim() || !canHaveChildren(selectedNodeId)}
								fullWidth
								sx={{ textTransform: 'none' }}
							>
								Add Level
							</MuiButton>
						</Box>
					</Paper>
				</Box>

				{/* Save Changes Section removed - confirmation dialog will be shown on Save click instead */}

				{/* Main Content Area */}
				<Box display="flex" gap={3}>
					{/* Left Side - Hierarchy Drag and Drop Tool */}
					<Box flex={1}>
						<h3 className={styles.formTitle} style={{ marginBottom: '16px' }}>
							Organization Hierarchy
						</h3>
						<Paper
							sx={{
								p: 2,
								minHeight: 400,
								backgroundColor: activeId ? 'action.hover' : 'background.paper',
								transition: 'background-color 0.2s ease',
							}}
						>
							{activeId && (
								<Box mb={2} p={1} sx={{ backgroundColor: 'info.light', borderRadius: 1 }}>
									<div style={{ fontSize: '14px', color: '#0d47a1' }}>
										🖱️ Dragging: <strong>{findNode(tree, activeId)?.name}</strong> - Drop on a valid
										target to move
									</div>
								</Box>
							)}
							<TreeView
								selectedItems={[selectedNodeId]}
								expandedItems={expanded}
								onSelectedItemsChange={(event, ids) => {
									// console.log('ids', ids);
									if (!(ids instanceof Array)) {
										setSelectedNodeId(String(ids) || '');
									} else {
										setSelectedNodeId(ids[0] || '');
									}
								}}
								onExpandedItemsChange={(event, ids) => setExpanded(ids)}
							>
								{renderTree(tree)}
							</TreeView>
						</Paper>
					</Box>

					{/* Right Side - Configuration Panel */}
					<Box flex={1}>
						{(() => {
							const selectedNode = findNode(tree, selectedNodeId);

							// Show workplace configuration if a workplace is selected
							if (selectedNode?.type === 'workplace') {
								return <WorkplaceConfiguration workplace={selectedNode} />;
							}

							// Show information panel for other node types
							return (
								<>
									<h3 className={styles.formTitle} style={{ marginBottom: '16px' }}>
										Selection Information
									</h3>
									<Paper sx={{ p: 2 }}>
										<Box display="flex" gap={2} flexDirection="column">
											{selectedNode ? (
												<>
													<div
														style={{
															fontSize: '16px',
															fontWeight: '500',
															color: '#333',
															marginBottom: '8px',
														}}
													>
														{selectedNode.type === 'organization'
															? '🏢'
															: selectedNode.type === 'custom'
																? '📁'
																: '🏭'}{' '}
														{selectedNode.name}
													</div>
													<div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
														Type:{' '}
														{selectedNode.type === 'organization'
															? 'Organization'
															: selectedNode.type === 'custom'
																? 'Custom Level'
																: 'Workplace'}
													</div>
													<div
														style={{
															fontSize: '14px',
															color: '#666',
															fontStyle: 'italic',
															marginBottom: '4px',
														}}
													>
														Path: {getNodePath(selectedNode.id)}
													</div>
													{selectedNode.type !== 'organization' && (
														<div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
															Parent: {findParentNode(tree, selectedNode.id)?.name || 'None'}
														</div>
													)}
													{selectedNode.children && selectedNode.children.length > 0 && (
														<div style={{ fontSize: '14px', color: '#666' }}>
															Children: {selectedNode.children.length}
														</div>
													)}
												</>
											) : (
												<div style={{ fontSize: '14px', color: '#666' }}>
													Select a node from the hierarchy to view its details.
												</div>
											)}
										</Box>
									</Paper>
								</>
							);
						})()}
					</Box>
				</Box>
			</Box>

			{/* Drag Overlay */}
			{/* Confirmation dialog shown when user clicks Save and there are pending changes */}
			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
				<DialogTitle>Confirm Save</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						You have {pendingMoves.length + pendingNewNodes.length} unsaved change(s). Click
						&quot;Save&quot; to apply them to the server.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmOpen(false)} disabled={isSaving}>
						Cancel
					</Button>
					<Button
						onClick={async () => {
							setConfirmOpen(false);
							await saveChanges();
						}}
						disabled={isSaving}
					>
						{isSaving ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
			<DragOverlay>
				{activeId ? (
					<Box
						sx={{
							p: 1,
							backgroundColor: 'primary.main',
							color: 'primary.contrastText',
							borderRadius: 1,
							boxShadow: 2,
							cursor: 'grabbing',
						}}
					>
						<div style={{ fontSize: '14px' }}>{findNode(tree, activeId)?.name}</div>
					</Box>
				) : null}
			</DragOverlay>
		</DndContext>
	);
};

export default HierarchyManager;
